import Head from 'next/head';
import '../styles/globals.css';
import { ToastProvider } from '../stores/ToastContext';
import { AuthProvider } from '../stores/AuthContext';
import { CmpProvider, useCmpSafe } from '../stores/CmpContext';
import CookieConsent from '../component/cookieConsent';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/nextjs';
import { setToken } from '../util/tokenManager';
import { useEffect, useRef } from 'react';
import { StyleProvider } from '@ant-design/cssinjs';
import { initializeOneLinkOnLoad } from '../util/oneLinkUtils';
import { I18nextProvider } from 'react-i18next';
import i18n from '@/util/i18n';
import { StoreContext, useStores } from '../stores/storeContext';
import { rootStore } from '../stores';
import LoginModal from '../component/modal/login';
import { observer } from 'mobx-react';

const DSN = 'https://b33a9d02d9e7ba69921d4a30bdec9c89@sentry.duetapp.net/2';
const noReplayPaths = [];
const remPath = [
  '/account',
  '/wing/account',
  '/log-entries/[method]',
  '/wing/log-entries/[method]',
  '/questionnaire',
  '/feedback',
  '/playground_mobile',
  '/pay-avalara',
  '/',
  '/wing',
  '/wing/features',
  '/wing/support',
];

const pixelTikTok = `!function (w, d, t) {
  w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
  ttq.load('CV8ETFJC77U13TBVPEU0');
  ttq.page();
}(window, document, 'ttq');`;

// ========== SDK 按 Consent 加载控制 ==========

// 初始化 Sentry（Analytics 类）
function initSentry(nodeEnv, route) {
  if (Sentry.getCurrentHub().getClient()) return; // 已初始化
  if (typeof window === 'undefined') return;

  const isLowSampleRate = noReplayPaths.includes(route);
  const { hostname } = window.location;
  const isLocalDev =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    hostname.startsWith('172.');
  const shouldEnableSentry = !isLocalDev;

    Sentry.init({
    environment: nodeEnv,
    dsn: shouldEnableSentry ? DSN : '',
      integrations: [
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        networkDetailAllowUrls: [window.location.origin],
        networkRequestHeaders: ['Authorization'],
        }),
      ],
    tracesSampleRate: isLowSampleRate ? 0.1 : 1,
    replaysSessionSampleRate: isLowSampleRate ? 0.1 : 1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// 加载 OneLink（Advertising 类）
function loadOneLink() {
  if (typeof window === 'undefined') return;
  if (window.AF_SMART_SCRIPT) {
    initializeOneLinkOnLoad();
    return;
  }
  const script = document.createElement('script');
  script.src = '/onelink-smart-script.js';
  script.async = true;
  script.onload = () => {
    initializeOneLinkOnLoad();
  };
  script.onerror = () => {
    console.warn('[CMP] OneLink Smart Script failed to load');
  };
  document.head.appendChild(script);
}

// 加载 TikTok Pixel（Advertising 类）
function loadTikTokPixel() {
  if (typeof window === 'undefined') return;
  if (window.ttq) return; // 已加载
  try {
    const script = document.createElement('script');
    script.innerHTML = pixelTikTok;
    document.head.appendChild(script);
  } catch (error) {
    console.warn('[CMP] Failed to load TikTok Pixel:', error);
  }
}

// ========== Consent-Aware SDK Loader Hook ==========
function useConsentBasedSDKs(pageProps, route) {
  const cmp = useCmpSafe();
  const loadedRef = useRef({ sentry: false, oneLink: false, tikTok: false });

  useEffect(() => {
    if (!cmp?.initialized) return;
    const { consent } = cmp;

    // Analytics: Sentry
    if (consent.analytics && !loadedRef.current.sentry) {
      loadedRef.current.sentry = true;
      initSentry(pageProps.nodeEnv, route);
    }

    // Advertising: OneLink + TikTok
    if (consent.advertising) {
      if (!loadedRef.current.oneLink) {
        loadedRef.current.oneLink = true;
        loadOneLink();
      }
      // TikTok 只在 welcome 页加载
      if (route === '/welcome/[gender]' && !loadedRef.current.tikTok) {
        loadedRef.current.tikTok = true;
        loadTikTokPixel();
      }
    }
  }, [cmp?.initialized, cmp?.consent, pageProps.nodeEnv, route]);
}

// ========== Main App ==========
function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const styleText = remPath.includes(router.pathname) || (pageProps.isWing && router.pathname === '/playground')
    ? `@media (max-width: 768px) {
    html {
      font-size: calc(100vw / 3.75) !important;
    }
  }
  @media (min-width: 769px) {
    html {
      font-size: calc(100vw / 14.4) !important;
    }
  }`
    : '';

  // 客户端渲染 存储服务端传过来的access-token
  if (typeof window !== 'undefined' && pageProps.accessToken) {
    setToken(pageProps.accessToken);
  }

  let appTitle = 'Duet';
  let iconHref = '/favicon.ico';
  let imageUrl =
    'https://cdn.duetapp.net/v1/images/eyJpZCI6Ikc1Q0JXR1dLTkVSS1I1Mk1OUVEyMk5BSk1QT1oyQSIsInciOjEwMjQsImgiOjEwMjQsImQiOjAsIm10IjoiaW1hZ2UvanBlZyIsImRoIjoxMjg0ODEwODc1MjU0MTYwMzIxMH0?format=originalOFGHLERTH';
  if (router.pathname.includes('/wing') || pageProps.isWing) {
    appTitle = 'Wing';
    iconHref =
      'https://cdn.duetapp.net/v1/images/eyJpZCI6Ik5EMzdJTElGRjU1VkZKMzNPUkhQS01KVjZYV0dPNCIsInciOjY0LCJoIjo2NCwiZCI6MCwibXQiOiJpbWFnZS9qcGVnIiwiZGgiOjk2ODc3ODg2MDg5OTAzNzYzNTB9.png';
    imageUrl = `${iconHref}?format=originalOFGHLERTH`;
  }

  const accountPagePath = ['/account', '/log-entries/[method]'];
  if (accountPagePath.includes(router.pathname) && typeof window !== 'undefined') {
    if (window.innerWidth > 768) {
      document.documentElement.style.setProperty('font-size', '110px', 'important');
  }
  }

  // 客户端渲染 测试和预发环境添加调试工具
  useEffect(() => {
    const shouldShowVConsole =
      pageProps.isDev ||
      pageProps.omiEnv === 'pre_prod' ||
      (typeof window !== 'undefined' && window.location.hostname === 'official-web.duet.dev.p1.cn') ||
      process.env.NODE_ENV === 'development';

    if (shouldShowVConsole) {
      const VConsole = require('vconsole');
      const vConsoleInstance = new VConsole();
      return () => {
        if (vConsoleInstance && typeof vConsoleInstance.destroy === 'function') {
          vConsoleInstance.destroy();
        }
      };
  }
  }, [pageProps.isDev, pageProps.omiEnv]);

  return (
    <StyleProvider hashPriority="high">
              <Head>
        <title>{`${appTitle} - Meet People You Resonate With`}</title>
                <meta charSet="utf-8" />
                <meta
                  name="viewport"
                  content="width=device-width,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no,viewport-fit=cover"
                />
        <link rel="icon" type="image/x-icon" href={iconHref} />
        <meta name="google-site-verification" content="4Gna40o8rIPOJbXNPxzISl0pEveNd46RTQ6QDmufIDg" />
        <meta
          name="description"
          content={`${appTitle} is a dating application helps you find meaningful relationships based on shared interests and experiences.`}
        />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:title" content={`${appTitle} | Meet People You Resonate With`} />
        <meta
          property="og:description"
          key="description"
          content={`${appTitle} is a dating application helps you find meaningful relationships based on shared interests and experiences.`}
        />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://duetapp.net/" />
        <meta name="twitter:title" content={`${appTitle} | Meet People You Resonate With`} />
        <meta
          name="twitter:description"
          content={`${appTitle} is a dating application helps you find meaningful relationships based on shared interests and experiences.`}
        />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <style>{styleText}</style>
              </Head>
      <CmpProvider>
        <I18nextProvider i18n={i18n}>
              <ToastProvider>
            <AuthProvider>
              <StoreContext.Provider value={{ rootStore }}>
                <AppContent Component={Component} pageProps={pageProps} route={router.route} />
              </StoreContext.Provider>
            </AuthProvider>
              </ToastProvider>
            </I18nextProvider>
      </CmpProvider>
    </StyleProvider>
  );
}

// 内部组件，可访问 CmpContext
function AppContent({ Component, pageProps, route }) {
  // 按 consent 加载 SDK
  useConsentBasedSDKs(pageProps, route);

  return (
    <>
      <Component {...pageProps} />
      <ModalRenderer />
      {route === '/' && <CookieConsent />}
    </>
  );
}

const ModalRenderer = observer(() => {
  const { rootStore } = useStores();
  const { curModal } = rootStore;

  if (curModal === 'login') {
    return <LoginModal curModal={curModal} />;
  }
  return null;
});

export default MyApp;
